'use client';

import { useRef, useEffect, useCallback } from 'react';
import Script from 'next/script';
import { styled } from 'styled-components';
import * as Sentry from '@sentry/nextjs';
import { HUBSPOT_PORTAL_ID } from '@lib/constants';
import { pushToDataLayer } from '@lib/gtm';

type HubSpotFormProps = {
  /**
   * The `formId` of the HubSpot form found in the embed code provided by HubSpot.
   * - Reach out to Marketing & Ops to get `formId`s
   */
  formId: string;
  /**
   * Optional title that appears above the form
   */
  title?: string;
  /**
   * Use this prop to specify which background color to use for the inset container. NOTE - we use an inset container to ensure form text is visible no matter the colorscheme of the page:
   * - `light` - This is the default. Display form in inset container with light background. Set form text color to be dark in HubSpot.
   * - `dark` - Display form in inset container with dark background. Set form text color to be white in HubSpot.
   */
  insetVariant?: 'dark' | 'light';
  /**
   * Optional className used to apply additional form styles
   */
  className?: string;
};

// NOTE: No padding is applied on our side because HubSpot applies 40px of padding to forms.
const HubSpotInsetContainer = styled.div<{
  $insetVariant: 'dark' | 'light';
}>`
  background-color: ${({ $insetVariant, theme }) =>
    $insetVariant === 'dark' ? theme.colors.blue700 : theme.colors.white};
  border-radius: ${({ theme }) => theme.radius.std};
`;

const HubSpotFormWrapper = styled.div<{
  $insetVariant: 'dark' | 'light';
}>`
  fieldset.form-columns-2 {
    max-width: unset;

    .hs-form-field input {
      width: calc(100% - ${({ theme }) => theme.spacing[0.75]});
    }
  }
`;

const FormTitle = styled.h3<{ $insetVariant: 'dark' | 'light' }>`
  color: ${({ $insetVariant, theme }) =>
    $insetVariant === 'dark' ? theme.colors.white : theme.colors.blue800};
  padding: ${({ theme }) => theme.spacing[2.5]} 40px; // Pixel value to match HubSpot's padding
  padding-bottom: 0;
  margin-bottom: 0;
  text-align: start;
`;

/**
 * HubSpot Form Component
 * Displays a HubSpot form given a valid `formId`. When adding a form, the form needs to be
 * styled within HubSpot to ensure that it matches either the `dark` or `light` insetVariant
 * of the `HubSpotForm` component.
 *
 * ## Rules
 * - We should never have two instances of the same form on a single page.
 * - HubSpot will overwrite the first form with the second form.
 * - Styles are applied to the form in HubSpot. We do not have control over the form styles.
 * - If you need a form to have white text this has do be done in HubSpot.
 *
 * Use the `insetVariant` prop to specify which background color to use:
 * - `light` - This is the default. Display form in inset container with light background. Set text color to be dark in HubSpot.
 * - `dark` - Display form in inset container with dark background. Set text color to be white in HubSpot.
 */
const HubSpotForm = ({
  formId,
  title,
  insetVariant = 'light',
  className,
}: HubSpotFormProps) => {
  // This is to track and prevent multiple form creations
  const hasInitialized = useRef(false);
  const formContainerRef = useRef(null);
  const uniqueFormId = `hubspotForm-${formId}`;

  const initializeForm = useCallback(() => {
    if (!window.hbspt || hasInitialized.current || !formContainerRef.current) {
      return;
    }
    try {
      window.hbspt.forms.create({
        portalId: HUBSPOT_PORTAL_ID,
        formId,
        target: `#${formContainerRef.current.id}`,
      });
      hasInitialized.current = true;
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'HubSpotForm',
          action: 'initializeForm',
        },
      });
    }
  }, [formId]);

  useEffect(() => {
    initializeForm();
  }, [initializeForm]);

  // Listen for HubSpot global form submission events
  // This appends a custom event to the data layer which can be used to trigger GTM tags when a form is submitted successfully.
  //
  // @see https://tagmanager.google.com/#/container/accounts/156064/containers/8021145/workspaces/1000244/triggers
  // @see https://developers.hubspot.com/docs/api-reference/global-form-events/guide#form-events
  useEffect(() => {
    const handleFormSubmitted = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.formId === formId) {
        pushToDataLayer({
          event: 'storefront.hubspot.onFormSubmitted',
          formId,
        });
      }
    };

    window.addEventListener(
      'hs-form-event:on-submission:success',
      handleFormSubmitted,
    );
    return () => {
      window.removeEventListener(
        'hs-form-event:on-submission:success',
        handleFormSubmitted,
      );
    };
  }, [formId]);

  return (
    <HubSpotInsetContainer $insetVariant={insetVariant} className={className}>
      <Script
        onLoad={() => {
          initializeForm();
        }}
        src="https://js.hsforms.net/forms/v2.js"
        strategy="lazyOnload"
      />
      {title ? (
        <FormTitle $insetVariant={insetVariant}>{title}</FormTitle>
      ) : null}
      <HubSpotFormWrapper
        $insetVariant={insetVariant}
        id={uniqueFormId}
        ref={formContainerRef}
      />
    </HubSpotInsetContainer>
  );
};

export default HubSpotForm;
